import { useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';

const dialogStyles = {
    '& .MuiDialog-paper': {
        borderRadius: 0,
        border: '3px solid #6366f1',
        backgroundColor: '#0f0f23',
        minWidth: 400,
    },
};

const selectStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 0,
        backgroundColor: '#1a1a2e',
        '& fieldset': {
            borderWidth: '2px',
            borderColor: '#374151',
        },
        '&:hover fieldset': {
            borderWidth: '2px',
            borderColor: '#6366f1',
        },
        '&.Mui-focused fieldset': {
            borderWidth: '3px',
            borderColor: '#6366f1',
        },
    },
    '& .MuiInputLabel-root': {
        fontWeight: 700,
        textTransform: 'uppercase',
        fontSize: '0.75rem',
        letterSpacing: '0.05em',
    },
};

interface GeneratePeriodsDialogProps {
    open: boolean;
    onClose: () => void;
    onGenerate: (year: number) => Promise<void>;
    currentYear: number;
    mode?: 'generate' | 'regenerate';
}

const GeneratePeriodsDialog = ({ open, onClose, onGenerate, currentYear, mode = 'generate' }: GeneratePeriodsDialogProps) => {
    const isRegenerate = mode === 'regenerate';
    const [year, setYear] = useState(currentYear);
    const [generating, setGenerating] = useState(false);

    const years = [];
    for (let y = currentYear - 2; y <= currentYear + 1; y++) {
        years.push(y);
    }

    const handleGenerate = async () => {
        setGenerating(true);
        try {
            await onGenerate(year);
            onClose();
        } finally {
            setGenerating(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} sx={dialogStyles}>
            <DialogTitle
                sx={{
                    fontWeight: 900,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '3px solid #374151',
                    pb: 2,
                }}
            >
                {isRegenerate ? 'Regenerate Tax Periods' : 'Generate Tax Periods'}
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
                {isRegenerate && (
                    <Typography
                        variant="body2"
                        sx={{
                            mb: 3,
                            p: 2,
                            border: '2px solid #f59e0b',
                            backgroundColor: 'rgba(245, 158, 11, 0.1)',
                            color: '#f59e0b',
                            fontWeight: 700,
                        }}
                    >
                        All existing periods for this year will be deleted and recreated from the current preset.
                    </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {isRegenerate
                        ? 'Select the year to regenerate tax periods for.'
                        : 'Generate tax payment periods for all components based on your tax preset configuration.'}
                </Typography>

                <FormControl fullWidth sx={selectStyles}>
                    <InputLabel id="year-label">Year</InputLabel>
                    <Select
                        labelId="year-label"
                        value={year}
                        onChange={(e) => setYear(e.target.value as number)}
                        label="Year"
                    >
                        {years.map((y) => (
                            <MenuItem key={y} value={y}>
                                {y}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions
                sx={{
                    p: 2,
                    borderTop: '3px solid #374151',
                }}
            >
                <Button
                    onClick={onClose}
                    disabled={generating}
                    sx={{
                        borderRadius: 0,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleGenerate}
                    disabled={generating}
                    sx={{
                        borderRadius: 0,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                    }}
                >
                    {generating
                        ? (isRegenerate ? 'Regenerating...' : 'Generating...')
                        : (isRegenerate ? 'Regenerate' : 'Generate')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default GeneratePeriodsDialog;
